import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/components/ToastProvider";
import { createBillingPortal, fetchBillingSummary, type BillingSummary } from "@/api/billing";
import { createInvite, listInvites, resendInvite } from "@/api/invites";
import type { InviteSummary } from "@/api/invites";
import { useArtistScope } from "@/lib/artistContext";
import { useSessionUser } from "@/hooks/useSessionUser";

const ROLE_OPTIONS = [
  { value: "member", label: "Member" },
  { value: "manager", label: "Manager" },
  { value: "artist", label: "Artist" },
  { value: "both", label: "Manager + Artist" },
] as const;

type AccountInvitesPanelProps = {
  organizationId?: string | null;
  organizationName?: string | null;
};

function formatDate(value?: string) {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
}

export default function AccountInvitesPanel({
  organizationId,
  organizationName,
}: AccountInvitesPanelProps) {
  const { sessionUser, session } = useSessionUser();
  const { artists, isMultiArtist, isLoading: artistsLoading, error: artistsError } = useArtistScope();
  const { push } = useToast();
  const normalizedRole = (sessionUser?.role ?? session?.role ?? "").toLowerCase();
  const isAdmin = ["owner", "admin"].includes(normalizedRole);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<typeof ROLE_OPTIONS[number]["value"]>("member");
  const [canViewFinancials, setCanViewFinancials] = useState(false);
  const [lastUrl, setLastUrl] = useState<string | null>(null);
  const [invites, setInvites] = useState<InviteSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [selectedArtistIds, setSelectedArtistIds] = useState<string[]>([]);
  const [billingSummary, setBillingSummary] = useState<BillingSummary | null>(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  const fetchInvites = useCallback(async () => {
    if (!isAdmin || !organizationId) return;
    setLoading(true);
    try {
      const data = await listInvites();
      setInvites(data);
    } catch (err) {
      push({ title: "Invites", message: err instanceof Error ? err.message : "Unable to load invites." });
    } finally {
      setLoading(false);
    }
  }, [isAdmin, organizationId, push]);

  useEffect(() => {
    void fetchInvites();
  }, [fetchInvites]);

  useEffect(() => {
    if (!isAdmin || !organizationId) return;
    let cancelled = false;
    const loadBilling = async () => {
      setBillingLoading(true);
      try {
        const summary = await fetchBillingSummary();
        if (!cancelled) {
          setBillingSummary(summary ?? null);
        }
      } catch (err) {
        if (!cancelled) {
          setBillingSummary(null);
        }
      } finally {
        if (!cancelled) {
          setBillingLoading(false);
        }
      }
    };
    void loadBilling();
    return () => {
      cancelled = true;
    };
  }, [isAdmin, organizationId]);

  useEffect(() => {
    if (!isMultiArtist) return;
    if (!artists.length) {
      setSelectedArtistIds([]);
      return;
    }
    if (artists.length === 1) {
      setSelectedArtistIds([artists[0].id]);
      return;
    }
    setSelectedArtistIds((prev) => (prev.length ? prev : artists.map((artist) => artist.id)));
  }, [artists, isMultiArtist]);

  const openBillingPortal = useCallback(async () => {
    setPortalLoading(true);
    try {
      const response = await createBillingPortal();
      if (response?.portal_url) {
        window.location.assign(response.portal_url);
      } else {
        throw new Error("Missing portal URL.");
      }
    } catch (err) {
      push({ title: "Billing", message: err instanceof Error ? err.message : "Unable to open billing portal." });
    } finally {
      setPortalLoading(false);
    }
  }, [push]);

  const handleCreate = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!email.trim()) return;
    if (!organizationId) {
      push({ title: "Invite failed", message: "Organization context is missing." });
      return;
    }
    if (isMultiArtist && selectedArtistIds.length === 0) {
      push({ title: "Invite failed", message: "Select at least one artist for this collaborator." });
      return;
    }
    const seatLimit = typeof billingSummary?.seat_limit === "number" ? billingSummary?.seat_limit : null;
    const seatCount = typeof billingSummary?.seat_count === "number" ? billingSummary?.seat_count : 0;
    if (seatLimit !== null && seatCount + 1 > seatLimit && !billingSummary?.billing_exempt) {
      push({
        title: "Add a seat",
        message: "Open the billing portal to add a seat, then resend the invite.",
      });
      await openBillingPortal();
      return;
    }
    setCreating(true);
    try {
      const response = await createInvite({
        email: email.trim(),
        role,
        organization_id: organizationId,
        artist_ids: isMultiArtist ? selectedArtistIds : undefined,
        can_view_financials: canViewFinancials,
      });
      setLastUrl(response.invite_url);
      push({ title: "Invite created", message: "Copy and share the link below." });
      setEmail("");
      setCanViewFinancials(false);
      await fetchInvites();
    } catch (err) {
      const payloadError =
        err instanceof Error
          ? (err as Error & { payload?: { detail?: { error?: string } } }).payload?.detail?.error
          : undefined;
      if (payloadError === "seat_limit_reached") {
        push({
          title: "Add a seat",
          message: "Open the billing portal to add a seat, then resend the invite.",
        });
        await openBillingPortal();
        return;
      }
      push({ title: "Invite failed", message: err instanceof Error ? err.message : "Unable to create invite." });
    } finally {
      setCreating(false);
    }
  };

  const handleResend = async (inviteId: string) => {
    setResendingId(inviteId);
    try {
      const payload = await resendInvite(inviteId);
      push({ title: "Invite resent", message: "Copy the link from the notification." });
      setLastUrl(payload.invite_url);
    } catch (err) {
      push({ title: "Resend failed", message: err instanceof Error ? err.message : "Unable to resend invite." });
    } finally {
      setResendingId(null);
    }
  };

  const copyLink = useCallback(
    async (url: string) => {
      if (typeof navigator === "undefined" || !navigator.clipboard) {
        push({ title: "Copy failed", message: "Clipboard unavailable in this browser." });
        return;
      }
      const normalizedUrl = url.startsWith("/") ? `${window.location.origin}${url}` : url;
      try {
        await navigator.clipboard.writeText(normalizedUrl);
        push({ title: "Copied", message: "Invite link saved to clipboard." });
      } catch (err) {
        push({ title: "Copy failed", message: err instanceof Error ? err.message : "Clipboard unavailable." });
      }
    },
    [push],
  );

  const inviteSection = useMemo(() => {
    if (!lastUrl) return null;
    const displayUrl = lastUrl.startsWith("/") ? `${window.location.origin}${lastUrl}` : lastUrl;
    return (
      <div className="aa-glass-card rounded-2xl border border-primary/40 bg-primary/10 px-4 py-3 text-sm text-foreground">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="break-all">{displayUrl}</p>
          <Button size="sm" variant="ghost" onClick={() => void copyLink(lastUrl)}>
            Copy link
          </Button>
        </div>
      </div>
    );
  }, [lastUrl, copyLink]);

  const allSelected = isMultiArtist && artists.length > 0 && selectedArtistIds.length === artists.length;
  const shouldShowArtistPicker = isMultiArtist && artists.length > 1;
  const selectedArtistNames = useMemo(() => {
    if (!selectedArtistIds.length) return [];
    const nameById = new Map(artists.map((artist) => [artist.id, artist.name || artist.id]));
    return selectedArtistIds.map((id) => nameById.get(id) ?? id);
  }, [artists, selectedArtistIds]);
  const selectedArtistLabel = useMemo(() => {
    if (!selectedArtistNames.length) return "Select artists";
    if (selectedArtistNames.length === artists.length) return `All artists (${selectedArtistNames.length})`;
    if (selectedArtistNames.length === 1) return selectedArtistNames[0];
    return `${selectedArtistNames.length} artists selected`;
  }, [artists.length, selectedArtistNames]);

  const toggleArtist = useCallback(
    (artistId: string) => {
      setSelectedArtistIds((prev) => {
        if (prev.includes(artistId)) {
          return prev.filter((value) => value !== artistId);
        }
        return [...prev, artistId];
      });
    },
    [setSelectedArtistIds],
  );

  const toggleAllArtists = useCallback(() => {
    if (allSelected) {
      setSelectedArtistIds([]);
    } else {
      setSelectedArtistIds(artists.map((artist) => artist.id));
    }
  }, [allSelected, artists]);

  return (
    <section className="aa-glass-card p-5 space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Team access</h2>
          <p className="text-sm text-muted-foreground">
            Invite collaborators to this account so they share contacts, leads, and settings. Each added user is a
            billed seat (monthly rate TBD).
          </p>
        </div>
        {organizationName && (
          <div className="rounded-full border border-border/60 px-3 py-1 text-xs uppercase tracking-wide text-muted-foreground">
            {organizationName}
          </div>
        )}
      </div>

      {!isAdmin && (
        <p className="text-sm text-muted-foreground">Only admins and owners can manage team invites.</p>
      )}
      {isAdmin && !organizationId && (
        <p className="text-sm text-rose-400">Organization context missing. Refresh the page to retry.</p>
      )}

      {isAdmin && organizationId && (
        <>
          <form onSubmit={handleCreate} className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-1 sm:col-span-2">
              <Label>Email address</Label>
              <Input
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                type="email"
                placeholder="collaborator@example.com"
                required
                className="w-full"
              />
              <p className="text-xs text-muted-foreground">
                Each invite must use a unique email. Existing users cannot be re-invited.
              </p>
            </div>
            <div className="space-y-1">
              <Label>Role</Label>
              <Select value={role} onValueChange={(value) => setRole(value as typeof role)}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Role" />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-3">
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-border/60 bg-background/40 p-4">
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Accounting access</p>
                  <p className="text-sm text-foreground">Allow this user to view financial data in Accounting.</p>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={canViewFinancials}
                    onCheckedChange={(value) => setCanViewFinancials(Boolean(value))}
                  />
                  <span className="text-xs text-muted-foreground">{canViewFinancials ? "Enabled" : "Disabled"}</span>
                </div>
              </div>
            </div>
            {isMultiArtist && (
              <div className="sm:col-span-3 space-y-3 rounded-2xl border border-border/60 bg-background/40 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Artist access</p>
                    <p className="text-sm text-foreground">
                      Select which artists this collaborator can access.
                    </p>
                  </div>
                  {shouldShowArtistPicker && (
                    <label className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Checkbox checked={allSelected} onCheckedChange={toggleAllArtists} />
                      Grant all artists
                    </label>
                  )}
                </div>
                {artistsLoading && <p className="text-xs text-muted-foreground">Loading artists…</p>}
                {artistsError && <p className="text-xs text-rose-400">{artistsError}</p>}
                {!artistsLoading && !artists.length && (
                  <p className="text-xs text-muted-foreground">No artists are linked to this organization yet.</p>
                )}
                {artists.length > 0 && !shouldShowArtistPicker && (
                  <p className="text-sm text-foreground">
                    This invite will include access to <strong>{artists[0]?.name || artists[0]?.id}</strong>.
                  </p>
                )}
                {artists.length > 0 && shouldShowArtistPicker && (
                  <div className="space-y-2">
                    <Label>Select artists</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-between">
                          <span className="truncate">{selectedArtistLabel}</span>
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent align="start" className="w-72">
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-xs uppercase tracking-wide text-muted-foreground">Artists</span>
                            <Button size="sm" variant="ghost" onClick={toggleAllArtists}>
                              {allSelected ? "Clear all" : "Select all"}
                            </Button>
                          </div>
                          <div className="grid gap-2">
                            {artists.map((artist) => {
                              const checked = selectedArtistIds.includes(artist.id);
                              return (
                                <label key={artist.id} className="flex items-center gap-2 text-sm text-foreground">
                                  <Checkbox checked={checked} onCheckedChange={() => toggleArtist(artist.id)} />
                                  <span className="truncate">{artist.name || artist.id}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  {selectedArtistIds.length || 0} artist{selectedArtistIds.length === 1 ? "" : "s"} selected.
                </p>
              </div>
            )}
            <div className="sm:col-span-3">
              <Button type="submit" className="w-full" disabled={creating || portalLoading || billingLoading}>
                {creating ? "Creating…" : portalLoading ? "Opening billing…" : "Create invite"}
              </Button>
              {inviteSection}
            </div>
          </form>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-foreground">Issued invites</h3>
              {loading && <p className="text-xs text-muted-foreground">Loading…</p>}
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead>Accepted</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invites.map((invite) => (
                  <TableRow key={invite.id}>
                    <TableCell>{invite.email}</TableCell>
                    <TableCell className="capitalize">{invite.role}</TableCell>
                    <TableCell>{formatDate(invite.created_at)}</TableCell>
                    <TableCell>{formatDate(invite.expires_at)}</TableCell>
                    <TableCell>{invite.accepted_at ? formatDate(invite.accepted_at) : "Pending"}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => invite.invite_url && void copyLink(invite.invite_url)}
                          disabled={!invite.invite_url}
                        >
                          Copy link
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={Boolean(invite.accepted_at) || Boolean(resendingId === invite.id)}
                          onClick={() => void handleResend(invite.id)}
                        >
                          {resendingId === invite.id ? "Resending…" : "Resend"}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {!invites.length && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-sm text-muted-foreground">
                      No invites issued yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </section>
  );
}
